import { FiActivity } from 'react-icons/fi';

export const zeroConditionalData = {
  courseId: 'zero-conditional',
  courseType: 'grammar',
  title: 'Zero Conditional',
  icon: FiActivity,
  overview: {
    impact: 'Talk about general truths, scientific facts, and reliable consequences.',
    focus: 'If + present simple, present simple',
    uses: 'Writing Task 2 (general truths), Speaking Part 3 (cause-effect), Listening (instructions)',
    duration: '15-20 minutes',
  },
  sections: [
    {
      title: 'Structure',
      content: `<p><strong>If / When + present simple, present simple</strong></p><p>Use the zero conditional when the result is <em>always</em> true given the condition. Both clauses use the present simple — never a future form.</p>`,
      examples: [
        { label: 'General truth', text: 'If you heat water to 100°C, it boils.' },
        { label: 'Scientific fact', text: 'Plants die if they don\'t get enough sunlight.' },
        { label: '"When" version', text: 'When ice melts, it turns into water.' },
      ],
    },
    {
      title: 'When to Use It',
      content: `<p>Reach for the zero conditional whenever the relationship between cause and effect is reliable.</p>`,
      points: [
        'Scientific or natural laws: "If you mix red and blue, you get purple."',
        'Habits or routines: "If I drink coffee at night, I can\'t sleep."',
        'Instructions and rules: "If the alarm rings, you leave the building immediately."',
        'General consequences: "When governments raise taxes, consumer spending drops."',
      ],
    },
    {
      title: 'Zero vs First Conditional',
      content: `<p>The two structures sound alike but have different meanings:</p>`,
      table: {
        headers: ['Structure', 'Meaning', 'Example'],
        rows: [
          ['Zero', 'Always true / general truth', 'If it rains, the streets get wet.'],
          ['First', 'Specific likely future event', 'If it rains tomorrow, I will stay home.'],
        ],
      },
      warning:
        'Don\'t use "will" in zero conditional sentences — the result is always true, not a future prediction.',
    },
    {
      title: 'IELTS Use Cases',
      content: `<p>How examiners want to hear it on test day:</p>`,
      points: [
        'Writing Task 2 — generalisations: "If young people are exposed to advertising early, they develop strong brand loyalties."',
        'Writing Task 1 (Process) — describing how a system works: "When the water reaches the upper chamber, it triggers the valve."',
        'Speaking Part 3 — cause-effect reasoning: "When students study abroad, they often gain more independence."',
      ],
    },
  ],
  exercises: [
    {
      title: 'Complete the Zero Conditional',
      type: 'select',
      instructions: 'Choose the correct form for each blank.',
      questions: [
        {
          text: 'If you heat ice, it ___ (melt).',
          options: ['melts', 'will melt', 'melted'],
          answer: 'melts',
          explanation:
            'Zero conditional uses present simple in BOTH clauses. The result is always true.',
        },
        {
          text: 'When the temperature ___ (drop), people stay indoors.',
          options: ['drops', 'will drop', 'is dropping'],
          answer: 'drops',
          explanation:
            'Both halves of a zero conditional sit in the present simple.',
        },
        {
          text: 'Plants die if they ___ (not / get) enough light.',
          options: ['don\'t get', 'won\'t get', 'didn\'t get'],
          answer: 'don\'t get',
          explanation:
            'Negative form of present simple in the if-clause.',
        },
        {
          text: 'If a country ___ (invest) in education, literacy rates ___ (rise).',
          options: [
            'invests / rise',
            'will invest / will rise',
            'invests / will rise',
          ],
          answer: 'invests / rise',
          explanation:
            'Both clauses stay in the present simple — this is a general consequence.',
        },
      ],
    },
    {
      title: 'Zero or First Conditional?',
      type: 'select',
      instructions: 'Pick the better choice for each context.',
      questions: [
        {
          text: 'You are explaining a scientific fact about water.',
          options: [
            'If you boil water, it evaporates.',
            'If you boil water, it will evaporate.',
            'If you will boil water, it evaporates.',
          ],
          answer: 'If you boil water, it evaporates.',
          explanation:
            'Scientific fact = always true → zero conditional.',
        },
        {
          text: 'You are predicting tomorrow\'s weather.',
          options: [
            'If it rains tomorrow, I stay home.',
            'If it rains tomorrow, I will stay home.',
            'If it will rain tomorrow, I stay home.',
          ],
          answer: 'If it rains tomorrow, I will stay home.',
          explanation:
            'A specific future event → first conditional.',
        },
      ],
    },
  ],
  tips: [
    'Both clauses are present simple — never use "will" with the zero conditional.',
    'You can swap "if" with "when" without changing the meaning.',
    'Use it for scientific facts, habits, rules, and general truths in IELTS Writing Task 2.',
    'It\'s a quick way to push your Grammatical Range score on Speaking Part 3 cause-and-effect questions.',
  ],
  dailyPractice: [
    'Write 3 zero conditional sentences about scientific or general truths each day.',
    'Spot zero vs first conditional in articles and essays you read.',
    'Practise rephrasing: "Heating ice melts it" → "If you heat ice, it melts."',
    'Use one zero conditional per IELTS Writing Task 2 generalisation paragraph.',
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar',
  aiContext:
    'Zero Conditional: if/when + present simple, present simple. Used for general truths, scientific facts, habits, and consequences. NOT used with "will". Common in IELTS Writing Task 2 (generalisations) and Speaking Part 3 (cause-effect reasoning).',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...zeroConditionalData,
      title: zeroConditionalData.title + ' (Foundations)',
      overview: { ...zeroConditionalData.overview, duration: '10-15 minutes' },
      sections: zeroConditionalData.sections.slice(0, 2),
      exercises: zeroConditionalData.exercises.slice(0, 1).map((ex) => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, 3) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...zeroConditionalData,
      title: zeroConditionalData.title + ' (Advanced)',
      overview: { ...zeroConditionalData.overview, duration: '20-25 minutes' },
      tips: [
        ...(zeroConditionalData.tips || []),
        'Band 8+: vary your subjects ("when researchers ___" / "if learners ___") to avoid repeating "if you" patterns.',
        'In writing, alternate "If X happens, Y follows" with "When X occurs, Y results" to broaden cohesion.',
      ],
    };
  }
  return zeroConditionalData;
}
